<script setup lang="ts">
import { computed } from 'vue'
import type { Tone } from '../tokens'
import type { ScopeNote } from '../types'
import { toneVars } from '../tone-vars'
import ScopeNotes from './ScopeNotes.vue'

/**
 * 页头。标题 + 副标题 + 口径说明入口 + 右侧操作区。
 *
 * 为什么「口径说明」挂在标题旁边而不是页脚:演示时的问题是**在看这个数的当下**
 * 提出来的。挂在页脚等于要人先滚到底,再滚回来看那张卡 —— 于是它就不会被点开。
 */
const props = withDefaults(
  defineProps<{
    title: string
    subtitle?: string
    notes?: readonly ScopeNote[]
    tone?: Tone
  }>(),
  { tone: 'light' },
)

const vars = computed(() => toneVars(props.tone))
const hasNotes = computed(() => (props.notes?.length ?? 0) > 0)
</script>

<template>
  <header class="page-header" :style="vars">
    <div class="main">
      <div class="title-row">
        <h1 class="title">{{ props.title }}</h1>
        <ScopeNotes v-if="hasNotes && props.notes" :notes="props.notes" :tone="props.tone" />
      </div>
      <p v-if="props.subtitle" class="subtitle">{{ props.subtitle }}</p>
    </div>
    <div class="actions">
      <slot name="actions" />
    </div>
  </header>
</template>

<style scoped>
.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 20px;
}

.main {
  min-width: 0;
}

.title-row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  line-height: 1.4;
  color: var(--sd-text1);
}

.subtitle {
  margin: 6px 0 0;
  font-size: 13px;
  color: var(--sd-text2);
}

.actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: none;
}
</style>
